import React from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import ExportButtons from '../components/ExportButtons';
import { DataTable } from '../../../components';
import { DataTableHeader } from '../../../components/DataTable/types';
import { formatDollars } from '../../../helpers/utilityFunctions';
import { GroupTransactionsData } from '../../../models/RevShareData';
import PeriodLabel from '../components/PeriodLabel';
import { pricebookHeaders, PricebookTable } from './PricebookTable';
import { TransactionsTableProps } from '../Transactions';
import TransactionsSkeleton from '../components/TransactionsSkeleton';
import TransactionsTableHeader from '../components/TransactionsTableHeader';
import {
  AmountPaidSubtotal,
  AmountPaidTotal,
  RevShareSubtotal,
  RevShareTotal,
} from '../components/TransactionsTotalsLabels';

interface GroupTransactionsTableProps extends TransactionsTableProps {
  data: GroupTransactionsData | undefined;
}

const groupHeaders: DataTableHeader[] = [
  { label: 'orgName', title: 'Org Name', flex: 1.5 },
  ...pricebookHeaders,
];

const GroupTransactionsTable: React.FC<GroupTransactionsTableProps> = ({
  data,
  startDate,
  endDate,
  onExportPdf,
  emptyStateText,
  loading,
}) => {
  const theme = useTheme();

  const formatTeamData = (team: any) => {
    return team.orgInfos.map((org: any) => ({
      id: org.id,
      orgName: (
        <Typography
          variant='p16Bold'
          color='secondary'
          sx={{
            whiteSpace: 'nowrap',
            position: 'absolute',
            overflow: 'hidden',
          }}>
          {org.orgName}
        </Typography>
      ),
      amountPaid: (
        <Typography variant='p16Bold' color='secondary'>
          {formatDollars(org.subscriptionTotalAmount ?? 0)}
        </Typography>
      ),
      revShareAmount: (
        <Typography variant='p16Bold' color='secondary'>
          {formatDollars(org.revShareTotalAmount ?? 0)}
        </Typography>
      ),
    }));
  };

  return (
    <Box>
      <TransactionsTableHeader
        headers={groupHeaders}
        totals={{
          amountPaid: <AmountPaidTotal total={data?.subscriptionTotalAmount ?? 0} />,
          revShareAmount: <RevShareTotal total={data?.revShareTotalAmount ?? 0} />,
        }}
        hasTrailingSpacer={true}
        leftContent={
          startDate &&
          endDate && (
            <Box>
              <PeriodLabel start={startDate} end={endDate} color='info.main' variant='p16' />
              <Box pt={2}>
                <ExportButtons start={startDate} end={endDate} onExportPdf={onExportPdf} />
              </Box>
            </Box>
          )
        }
      />

      {loading ? (
        <TransactionsSkeleton />
      ) : (
        <Stack spacing={4}>
          {data?.teamInfos?.length ? (
            data.teamInfos.map(team => (
              <Box key={team.id}>
                <DataTable
                  lastColumnElement='spacer'
                  headers={groupHeaders}
                  data={formatTeamData(team)}
                  expandedContent={row => {
                    const org = team.orgInfos.find(o => o.id === row.id);
                    if (!org) return null;

                    return (
                      <PricebookTable
                        data={org.pricebookInfos}
                        headers={groupHeaders}
                        rowStyles={{ backgroundColor: '#fff' }}
                        lastColumnElement='spacer'
                        emptyStateText={emptyStateText}
                      />
                    );
                  }}
                  leftElement={
                    <Typography variant='p18SemiBold' color='secondary.main'>
                      {team.teamName}
                    </Typography>
                  }
                  rightElement={{
                    alignWithColumns: true,
                    values: {
                      amountPaid: <AmountPaidSubtotal total={team.subscriptionTotalAmount} />,
                      revShareAmount: <RevShareSubtotal total={team.revShareTotalAmount} />,
                    },
                  }}
                  emptyStateText={emptyStateText}
                  borderOverride={{
                    background: `linear-gradient(to right, transparent 20px, ${theme.palette.info.main} 20px, ${theme.palette.info.main} calc(100% - 20px), transparent calc(100% - 20px)) bottom/100% 1px no-repeat`,
                    backgroundPosition: 'left calc(100% - 5px)',
                  }}
                  rowStyles={{ backgroundColor: '#fff' }}
                />
              </Box>
            ))
          ) : (
            <DataTable data={[]} headers={groupHeaders} emptyStateText={emptyStateText} />
          )}
        </Stack>
      )}
    </Box>
  );
};

export default GroupTransactionsTable;
